import { computed, type ComputedRef, type Ref } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

/**
 * The fields we compare between two contracts. All optional — missing
 * values show as an em-dash in the rendered diff.
 */
export type ContractSnapshot = {
  endDate?: string | null
  startDate?: string | null
  rate?: number | null
  rateType?: string | null
  currency?: string | null
} | null | undefined

export type ContractDiffRow = {
  label: string
  from: string
  to: string
}

export const formatRate = (c: ContractSnapshot): string => {
  if (!c) return ''
  const parts: string[] = []
  if (c.currency) parts.push(c.currency)
  if (c.rate !== null && c.rate !== undefined) parts.push(String(c.rate))
  const rateType = c.rateType ? `/ ${c.rateType.toLowerCase()}` : ''
  return `${parts.join(' ')} ${rateType}`.trim()
}

/**
 * Returns one row per field that differs between the active and draft
 * contract. Returns an empty list when either contract is missing —
 * callers should hide the diff section in that case.
 */
export function useContractDiff(
  active: Ref<ContractSnapshot> | ComputedRef<ContractSnapshot>,
  draft: Ref<ContractSnapshot> | ComputedRef<ContractSnapshot>,
): ComputedRef<ContractDiffRow[]> {
  return computed(() => {
    const a = active.value
    const d = draft.value
    if (!a || !d) return []

    const rows: ContractDiffRow[] = []

    if (a.endDate !== d.endDate) {
      rows.push({ label: $t('End date'), from: a.endDate ?? '—', to: d.endDate ?? '—' })
    }
    if (a.startDate !== d.startDate) {
      rows.push({ label: $t('Start date'), from: a.startDate ?? '—', to: d.startDate ?? '—' })
    }
    const activeRate = formatRate(a)
    const draftRate = formatRate(d)
    if (activeRate !== draftRate) {
      rows.push({ label: $t('Rate'), from: activeRate || '—', to: draftRate || '—' })
    }

    return rows
  })
}
